import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWithdraw } from '../../hooks/useWithdraw';
import { useAuth } from '../../context/AuthContext';
import SuccessModal from '../../components/common/UI/SuccessModal';
import './Withdraw.css';

// Mirrors the backend tiers in withdraw/services.py::calculate_bank_withdrawal_fee
const calculateFee = (amount) => {
  const amt = parseFloat(amount);
  if (isNaN(amt) || amt <= 0) return 0;
  if (amt >= 1000000) return 1000;
  if (amt >= 10000) return 200;
  return 100;
};

const BankWithdrawal = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { createBankWithdrawal, loading, error, isSuccess, successDetails, hideSuccess } = useWithdraw();

  const [formData, setFormData] = useState({
    amount: ''
  });

  const [defaultAccount, setDefaultAccount] = useState(null);
  const [formError, setFormError] = useState('');
  const [networkError, setNetworkError] = useState('');

  // Load default bank account from user profile
  useEffect(() => {
    if (user?.profile?.bank_accounts) {
      const account = user.profile.bank_accounts.find(acc => acc.is_default);
      setDefaultAccount(account || null);
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setFormError('');
    setNetworkError('');
  };

  const fee = calculateFee(formData.amount);
  const amountNum = parseFloat(formData.amount) || 0;
  const totalToDeduct = amountNum > 0 ? amountNum + fee : 0;
  const userBalance = user?.wallet?.balance ? parseFloat(user.wallet.balance) : null;
  const insufficientBalance =
    userBalance !== null && totalToDeduct > 0 && totalToDeduct > userBalance;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setNetworkError('');

    // Check if user has default bank account
    if (!defaultAccount) {
      setFormError(
        'No default bank account found. Please go to Profile → Settings → Bank Accounts to add and set a default bank account.'
      );
      return;
    }

    // Validation
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setFormError('Please enter a valid amount');
      return;
    }

    if (insufficientBalance) {
      setFormError(`Insufficient balance. You need ₦${totalToDeduct.toLocaleString()} (amount + ₦${fee} fee).`);
      return;
    }

    try {
      // Withdraw using default account - no manual bank details needed
      await createBankWithdrawal(parseFloat(formData.amount));

      // Clear amount on success
      setFormData({
        amount: ''
      });

    } catch (err) {
      // Handle specific error messages
      if (err.error && err.error.includes('No default bank account')) {
        setFormError(
          'No default bank account found. Please go to Profile → Settings → Bank Accounts to add and set a default bank account before withdrawing.'
        );
      } else {
        setNetworkError(err.error || 'Withdrawal failed. Please try again.');
      }
    }
  };

  const handleGoToSettings = () => {
    navigate('/profile', { state: { activeTab: 'settings' } });
  };

  return (
    <div className="withdraw-page">
      <div className="withdraw-header">
        <button className="back-link" onClick={() => navigate(-1)}>
          <span className="back-arrow">←</span> Back to Dashboard
        </button>
      </div>

      <div className="withdraw-header-mobile">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <span className="back-icon">←</span> Back
        </button>
      </div>

      <div className="withdraw-container">
        <div className="withdraw-card">
          <h1 className="withdraw-title">Bank Withdrawal</h1>

          {!defaultAccount ? (
            <div className="no-account-message">
              <div className="no-account-icon">🏦</div>
              <h3>No Default Bank Account</h3>
              <p>
                You need to set up a default bank account before you can withdraw funds.
              </p>
              <div className="no-account-instructions">
                <p><strong>To add a bank account:</strong></p>
                <ol>
                  <li>Go to Profile → Settings</li>
                  <li>Click on "Bank Accounts" section</li>
                  <li>Click "Add Bank" to add your bank details</li>
                  <li>Set it as your default account</li>
                  <li>Return here to withdraw</li>
                </ol>
              </div>
              <button 
                className="settings-btn"
                onClick={handleGoToSettings}
              >
                Go to Settings
              </button>
            </div>
          ) : (
            <>
              {/* Display Default Account Information */}
              <div className="default-account-info">
                <div className="account-info-header">
                  <span className="account-icon">🏦</span>
                  <h3>Default Bank Account</h3>
                  <span className="default-badge">Default</span>
                </div>
                <div className="account-details">
                  <div className="account-detail-row">
                    <span className="detail-label">Bank Name:</span>
                    <span className="detail-value">{defaultAccount.bank_name}</span>
                  </div>
                  <div className="account-detail-row">
                    <span className="detail-label">Account Name:</span>
                    <span className="detail-value">{defaultAccount.account_name}</span>
                  </div>
                  <div className="account-detail-row">
                    <span className="detail-label">Account Number:</span>
                    <span className="detail-value">{defaultAccount.account_number}</span>
                  </div>
                </div>
                <p className="account-note">
                  Withdrawals will be sent to this account. To change it, go to 
                  <button className="inline-link" onClick={handleGoToSettings}>
                    Profile → Settings
                  </button>
                </p>
              </div>

              <form onSubmit={handleSubmit} className="withdraw-form">
                <div className="form-group">
                  <label>Amount (₦)</label>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                    min="1000"
                    className="form-input"
                    required
                  />
                  <p className="input-hint">
                    Minimum withdrawal is ₦1,000.
                  </p>
                </div>

                {amountNum > 0 && (
                  <div className="fee-breakdown">
                    <div className="fee-breakdown-row">
                      <span>Withdrawal amount:</span>
                      <span>₦{amountNum.toLocaleString()}</span>
                    </div>
                    <div className="fee-breakdown-row">
                      <span>Withdrawal fee:</span>
                      <span>₦{fee.toLocaleString()}</span>
                    </div>
                    <div className="fee-breakdown-row fee-breakdown-total">
                      <span>Total deducted from wallet:</span>
                      <span>₦{totalToDeduct.toLocaleString()}</span>
                    </div>
                  </div>
                )}

                {insufficientBalance && (
                  <div className="error-message">
                    Insufficient balance. This withdrawal requires ₦{totalToDeduct.toLocaleString()}.
                  </div>
                )}

                {formError && <div className="error-message">{formError}</div>}
                {networkError && <div className="error-message">{networkError}</div>}
                {error && <div className="error-message">{error}</div>}

                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={loading || insufficientBalance}
                >
                  {loading ? 'Submitting...' : 'Submit Withdrawal'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={isSuccess}
        onClose={hideSuccess}
        title="Withdrawal Request Submitted!"
        message="Your bank withdrawal request has been received and is being processed."
        details={successDetails}
        onContinue={() => {
          hideSuccess();
          navigate('/transaction-history');
        }}
      />
    </div>
  );
};

export default BankWithdrawal;